import React, { useEffect, useState } from "react";
import { X, AlertOctagon, Calendar, Wrench, ListChecks } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/AuthContext";
import { api } from "@/lib/api";

const DISMISS_KEY = "today-popup-dismissed";

// Prefixos de rotas públicas onde este popup NUNCA deve aparecer.
const PUBLIC_PATH_PREFIXES = ["/p/", "/proposta/", "/login"];
const isPublicPath = (p) => {
    if (!p) return false;
    if (p === "/p" || p === "/proposta" || p === "/login") return true;
    return PUBLIC_PATH_PREFIXES.some((x) => p.startsWith(x));
};

const todayStr = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

export default function TodayPopup() {
    const { user } = useAuth();
    const location = useLocation();
    const [data, setData] = useState(null);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        // Defesa interna: nunca correr em rotas públicas, mesmo que algo
        // este componente seja renderizado por engano fora do contexto admin.
        if (isPublicPath(location.pathname)) return;
        if (!user) return;
        let cancelled = false;
        const check = async () => {
            // Já foi dispensado hoje?
            const dismissed = (() => { try { return localStorage.getItem(DISMISS_KEY); } catch { return null; } })();
            if (dismissed === todayStr()) return;

            try {
                const today = todayStr();
                const [year, month] = today.split("-");
                const r = await api.get(`/calendar/counts?year=${year}&month=${month}`);
                const counts = r.data?.counts || {};
                const t = counts[today] || {};
                if (cancelled) return;
                const totalToday = (t.tasks || 0) + (t.instalacoes || 0);
                // Atrasadas: somar dias anteriores deste mês com tarefas
                let atrasadas = 0;
                for (const [k, v] of Object.entries(counts)) {
                    if (k < today) atrasadas += (v.tasks || 0);
                }
                if (totalToday > 0 || atrasadas > 0) {
                    setData({ today: t, atrasadas, dateStr: today });
                    setOpen(true);
                }
            } catch (_) { /* silencioso */ }
        };
        // Atrasa 1.5s para não interromper logo ao entrar
        const timer = setTimeout(check, 1500);
        return () => { cancelled = true; clearTimeout(timer); };
    }, [user, location.pathname]);

    const dismiss = () => {
        try { localStorage.setItem(DISMISS_KEY, todayStr()); } catch { /* ignore */ }
        setOpen(false);
    };

    if (!open || !data) return null;
    // Defesa final no render: bail-out se a rota for pública.
    if (isPublicPath(location.pathname)) return null;

    const { today, atrasadas, dateStr } = data;
    const dia = new Date(dateStr).toLocaleDateString("pt-PT", {
        weekday: "long", day: "2-digit", month: "long",
    });

    return (
        <div className="fixed inset-0 bg-black/40 z-[60] flex items-center justify-center p-4 animate-in fade-in">
            <div className="bg-white rounded-lg w-full max-w-md shadow-2xl overflow-hidden">
                <div className="bg-gradient-to-r from-[#002FE6] to-[#1E3FFF] text-white px-5 py-4 flex items-center justify-between">
                    <div>
                        <div className="text-[11px] uppercase tracking-wider opacity-80">Resumo do dia</div>
                        <div className="font-bold text-lg capitalize">{dia}</div>
                    </div>
                    <button onClick={dismiss} className="p-1 hover:bg-white/20 rounded">
                        <X size={20} />
                    </button>
                </div>
                <div className="p-5 space-y-3">
                    {atrasadas > 0 && (
                        <div className="flex items-start gap-3 p-3 bg-red-50 border border-red-200 rounded-md">
                            <AlertOctagon className="text-red-600 shrink-0" size={20} />
                            <div className="flex-1">
                                <div className="font-bold text-red-900">{atrasadas} tarefa{atrasadas > 1 ? "s" : ""} atrasada{atrasadas > 1 ? "s" : ""}</div>
                                <div className="text-xs text-red-700">Tens prazos em aberto de dias anteriores.</div>
                            </div>
                        </div>
                    )}
                    {(today.tasks || 0) > 0 && (
                        <div className="flex items-start gap-3 p-3 bg-amber-50 border border-amber-200 rounded-md">
                            <ListChecks className="text-amber-600 shrink-0" size={20} />
                            <div className="flex-1">
                                <div className="font-bold text-amber-900">{today.tasks} tarefa{today.tasks > 1 ? "s" : ""} para hoje</div>
                                <div className="text-xs text-amber-700">Kommo · contactos, instalações, follow-ups</div>
                            </div>
                        </div>
                    )}
                    {(today.instalacoes || 0) > 0 && (
                        <div className="flex items-start gap-3 p-3 bg-emerald-50 border border-emerald-200 rounded-md">
                            <Wrench className="text-emerald-700 shrink-0" size={20} />
                            <div className="flex-1">
                                <div className="font-bold text-emerald-900">{today.instalacoes} instalação{today.instalacoes > 1 ? "ões" : ""} marcada{today.instalacoes > 1 ? "s" : ""}</div>
                                <div className="text-xs text-emerald-700">Confirma com clientes.</div>
                            </div>
                        </div>
                    )}
                    {(today.tasks || 0) === 0 && (today.instalacoes || 0) === 0 && atrasadas === 0 && (
                        <div className="text-center text-zinc-500 py-2">
                            <Calendar size={28} className="mx-auto mb-2 text-emerald-500" />
                            <div className="font-semibold">Sem pendências hoje 🎉</div>
                        </div>
                    )}
                </div>
                <div className="border-t border-zinc-200 p-3 flex items-center justify-end gap-2 bg-zinc-50">
                    <button
                        onClick={dismiss}
                        className="px-4 py-2 text-sm font-semibold text-zinc-600 hover:text-zinc-900"
                        data-testid="today-dismiss"
                    >
                        OK, vi
                    </button>
                    <Link
                        to="/instalacoes"
                        onClick={dismiss}
                        className="px-4 py-2 bg-[#002FE6] hover:bg-[#0024B5] text-white text-sm font-semibold rounded-md"
                    >
                        Ver instalações →
                    </Link>
                </div>
            </div>
        </div>
    );
}
